import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import type {WorkspaceMobileBottomCreateItem} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceMobileBottomCreateItem";
import type {ListModuleConfig, ListJourneyColumnIds, WorkspaceListCreateItemsContext, WorkspaceListDispositionSetup} from "~/domainModels/listView/listModuleConfig";
import {
    AMARON__CALL__SCHEDULE__FORM__ID,
    AmaronEventTypes,
    AmaronLeadStatusEnum,
    amaronLeadStatuses,
    getAmaronListEventCreateItems,
    getAmaronListModuleConfig,
    isAmaronWorkspace,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID,
    AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    AMARON_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    AMARON_JOURNEY_TABLE_VISIBLE_COLUMN_IDS,
    AMARON_SERVICE_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    AMARON_DEFAULT_JOURNEY_TRIGGER_CONFIG,
    amaronCallStatuses,
    amaronDispositionConfig,
    amaronEnquiryStatuses,
    amaronServiceCategories,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    DuluxEventTypes,
    DuluxLeadStatusEnum,
    getDuluxListEventCreateItems,
    getDuluxListModuleConfig,
    getDuluxListScheduleRouteForEventTemplate,
    isDuluxWorkspace,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__NAME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID,
    DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID,
    DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID,
    DULUX_APPOINTMENTS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_ONBOARDING_EVENT_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_JOURNEY_TABLE_VISIBLE_COLUMN_IDS,
    DULUX_DEFAULT_JOURNEY_TRIGGER_CONFIG,
    appointmentStatuses,
    callStatuses,
    duluxDispositionConfig,
    enquiryStatuses,
    leadStatuses,
    onboardingStatuses,
} from "~/domainModels/workspaceSpecific/dulux";
import {isEmotoradWorkspace} from "~/domainModels/workspaceSpecific/emotorad";
import {
    getAmaronEventKanbanColumnConfig,
    getAmaronJourneyLeadsKanbanColumnConfig,
    getAmaronInQueueKanbanColumnConfig,
} from "~/domainModels/amaronCalendarStatusColors";

import type {ListCardStatus, ListStatusPillType} from "~/components/ottopilotDataViewer/listStatusConfig";
import {
    createInQueueAssignmentListStatusColumnConfig,
    inferListStatusPillType,
    LIST_INQUEUE_ASSIGNMENT_STATUS,
    listStatusPillTypeToLozengeStyle,
    resolveListCardStatusFromColumnConfig,
    type ListStatusBoardConfig,
} from "~/components/ottopilotDataViewer/listStatusConfig";
import type {EventWithEventContainer, EventContainerWithEvents} from "~/utilities/apis/api";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {resolveConfigFormHref} from "~/workspaceConfig/configHref";

function listStatusFromPillType(name: string, pillType: ListStatusPillType): ListCardStatus {
    return {
        name,
        lozengeStyle: listStatusPillTypeToLozengeStyle(pillType),
    };
}

const IN_QUEUE_LIST_STATUS_BOARD: ListStatusBoardConfig = {
    columnConfig: createInQueueAssignmentListStatusColumnConfig(),
};

export type JourneysListPageKey = "leads" | "inQueue";
export type EventsListPageKey = "events";

export type WorkspaceListSupport = {
    journeys: Partial<Record<JourneysListPageKey, ListModuleConfig>>;
    events: ListModuleConfig | null;
    journeyColumnIds: ListJourneyColumnIds;
    journeyLeadsTableVisibleColumnIds: readonly string[];
    disposition: WorkspaceListDispositionSetup;
    isListEventTemplate: (eventTemplateId: Uuid) => boolean;
    getEventTableVisibleColumnIds: (eventTemplateId: Uuid) => readonly string[] | null;
    getEventScheduleRoute: (eventTemplateId: Uuid, workspaceId: Uuid) => string | null;
    getListCreateItems: (ctx: WorkspaceListCreateItemsContext) => readonly WorkspaceMobileBottomCreateItem[];
    resolveJourneyListStatus: (
        journey: EventContainerWithEvents,
        ctx: {page: JourneysListPageKey; resolveInQueueAssignmentLabel?: (j: EventContainerWithEvents) => "Assigned" | "Unassigned"},
    ) => ListCardStatus;
    resolveEventListStatus: (event: EventWithEventContainer, eventTemplateId: Uuid) => ListCardStatus;
    getJourneysListStatusBoard?: (page: JourneysListPageKey) => ListStatusBoardConfig | null;
    getEventsListStatusBoard?: (eventTemplateId: Uuid) => ListStatusBoardConfig | null;
};

export function resolveStatusFromRecord(
    rawValue: unknown,
    record: Record<string, {displayName: string}>,
    fallback: string,
): string {
    if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") {
        return fallback;
    }
    const raw = String(rawValue).trim();
    return record[raw]?.displayName ?? raw;
}

function resolveLeadStatusPill(idStr: string, journey: EventContainerWithEvents, churnedIds: string[]): ListStatusPillType {
    if (churnedIds.some((id) => idStr === id)) {
        return "Archived";
    }
    if (idStr === "" && journey.status !== OttopilotEventContainerStatus.active) {
        return journey.status === OttopilotEventContainerStatus.archived
            ? "Archived"
            : "Deleted";
    }
    return "Active";
}

const AMARON_LIST_SUPPORT: WorkspaceListSupport = {
    journeys: {
        leads: getAmaronListModuleConfig("journeysLeads"),
        inQueue: getAmaronListModuleConfig("journeysInQueue"),
    },
    events: getAmaronListModuleConfig("events"),
    journeyColumnIds: {
        contactNumber: AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
        remarks: AMARON__EVENT_CONTAINER__REMARKS__COLUMN_ID,
        leadStatus: AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    },
    journeyLeadsTableVisibleColumnIds: AMARON_JOURNEY_TABLE_VISIBLE_COLUMN_IDS,
    disposition: {
        triggerConfiguration: AMARON_DEFAULT_JOURNEY_TRIGGER_CONFIG,
        dispositionConfiguration: amaronDispositionConfig,
        dispositionUiLevels: 4,
        getLeadDisplayName: (event) => {
            const raw = event.eventContainer.additionalColumnValues[AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID];
            if (typeof raw === "string" && raw.trim() !== "") {
                return raw.trim();
            }
            return "Lead";
        },
    },
    isListEventTemplate: isAmaronListEventTemplate,
    getEventTableVisibleColumnIds: (eventTemplateId) => {
        if (eventTemplateId === (AmaronEventTypes.Calls as Uuid)) {
            return AMARON_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (eventTemplateId === (AmaronEventTypes.Enquiry as Uuid)) {
            return AMARON_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (eventTemplateId === (AmaronEventTypes.Service as Uuid)) {
            return AMARON_SERVICE_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        return null;
    },
    getEventScheduleRoute: (_eventTemplateId, workspaceId) =>
        resolveConfigFormHref({workspaceId, formId: AMARON__CALL__SCHEDULE__FORM__ID}) ?? "",
    getListCreateItems: (ctx) => getAmaronListEventCreateItems(ctx.workspaceMobileNavigationConfiguration),
    resolveEventListStatus: (event, eventTemplateId) => {
        const columnConfig = getAmaronEventKanbanColumnConfig(eventTemplateId);
        if (columnConfig === null) {
            return listStatusFromPillType("Active", "Active");
        }
        if (eventTemplateId === (AmaronEventTypes.Calls as Uuid)) {
            const statusValue = event.additionalColumnValues[AMARON__EVENT__CALLS__CALL_STATUS__COLUMN_ID];
            return resolveListCardStatusFromColumnConfig({
                statusValue,
                columnConfig,
                fallbackDisplayName: "Active",
                resolveDisplayName: ({statusValue: raw}) =>
                    resolveStatusFromRecord(raw, amaronCallStatuses, "Active"),
            });
        }
        if (eventTemplateId === (AmaronEventTypes.Enquiry as Uuid)) {
            const statusValue = event.additionalColumnValues[AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID];
            return resolveListCardStatusFromColumnConfig({
                statusValue,
                columnConfig,
                fallbackDisplayName: "Open",
                resolveDisplayName: ({statusValue: raw}) =>
                    resolveStatusFromRecord(raw, amaronEnquiryStatuses, "Open"),
            });
        }
        if (eventTemplateId === (AmaronEventTypes.Service as Uuid)) {
            const statusValue = event.additionalColumnValues[AMARON__EVENT__SERVICE__CATEGORY__COLUMN_ID];
            return resolveListCardStatusFromColumnConfig({
                statusValue,
                columnConfig,
                fallbackDisplayName: "Booked",
                resolveDisplayName: ({statusValue: raw}) =>
                    resolveStatusFromRecord(raw, amaronServiceCategories, "Booked"),
            });
        }
        return listStatusFromPillType("Active", "Active");
    },
    getJourneysListStatusBoard: (page) => {
        if (page === "inQueue") {
            return {columnConfig: getAmaronInQueueKanbanColumnConfig()};
        }
        if (page === "leads") {
            return {columnConfig: getAmaronJourneyLeadsKanbanColumnConfig()};
        }
        return null;
    },
    getEventsListStatusBoard: (eventTemplateId) => {
        const columnConfig = getAmaronEventKanbanColumnConfig(eventTemplateId);
        if (columnConfig === null) {
            return null;
        }
        return {columnConfig};
    },
    resolveJourneyListStatus: (journey, ctx) => {
        if (ctx.page === "inQueue" && ctx.resolveInQueueAssignmentLabel !== undefined) {
            const label = ctx.resolveInQueueAssignmentLabel(journey);
            return resolveListCardStatusFromColumnConfig({
                statusValue:
                    label === "Assigned"
                        ? LIST_INQUEUE_ASSIGNMENT_STATUS.assigned
                        : LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned,
                columnConfig: getAmaronInQueueKanbanColumnConfig(),
            });
        }
        const raw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID];
        return resolveListCardStatusFromColumnConfig({
            statusValue: raw,
            columnConfig: getAmaronJourneyLeadsKanbanColumnConfig(),
            fallbackDisplayName: amaronLeadStatuses[AmaronLeadStatusEnum.LIVE].displayName,
            resolveDisplayName: ({statusValue}) => {
                const idStr = statusValue.trim();
                if (idStr === "") {
                    return amaronLeadStatuses[AmaronLeadStatusEnum.LIVE].displayName;
                }
                return Object.values(amaronLeadStatuses).find((status) => String(status.id) === idStr)?.displayName ?? idStr;
            },
        });
    },
};

const DULUX_LIST_SUPPORT: WorkspaceListSupport = {
    journeys: {
        leads: getDuluxListModuleConfig("journeysLeads"),
    },
    events: getDuluxListModuleConfig("events"),
    journeyColumnIds: {
        contactNumber: DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
        remarks: DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID,
        leadStatus: DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    },
    journeyLeadsTableVisibleColumnIds: DULUX_JOURNEY_TABLE_VISIBLE_COLUMN_IDS,
    disposition: {
        triggerConfiguration: DULUX_DEFAULT_JOURNEY_TRIGGER_CONFIG,
        dispositionConfiguration: duluxDispositionConfig,
        dispositionUiLevels: 4,
        getLeadDisplayName: (event) => {
            const raw = event.eventContainer.additionalColumnValues[DULUX__EVENT_CONTAINER__NAME__COLUMN_ID];
            if (typeof raw === "string" && raw.trim() !== "") {
                return raw.trim();
            }
            return "Lead";
        },
    },
    isListEventTemplate: isDuluxListEventTemplate,
    getEventTableVisibleColumnIds: (eventTemplateId) => {
        if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
            return DULUX_CALLS_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
            return DULUX_APPOINTMENTS_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (eventTemplateId === (DuluxEventTypes.Enquiry as Uuid)) {
            return DULUX_ENQUIRY_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        if (eventTemplateId === (DuluxEventTypes.Onboarding as Uuid)) {
            return DULUX_ONBOARDING_EVENT_TABLE_VISIBLE_COLUMN_IDS;
        }
        return null;
    },
    getEventScheduleRoute: (eventTemplateId, workspaceId) => getDuluxListScheduleRouteForEventTemplate(workspaceId, eventTemplateId),
    getListCreateItems: (ctx) => getDuluxListEventCreateItems(ctx.workspaceMobileNavigationConfiguration, ctx.eventTemplateId),
    resolveEventListStatus: (event, eventTemplateId) => {
        let statusName = "";
        if (eventTemplateId === (DuluxEventTypes.Calls as Uuid)) {
            statusName = resolveStatusFromRecord(
                event.additionalColumnValues[DULUX__EVENT__CALLS__CALL_STATUS__COLUMN_ID],
                callStatuses,
                "",
            );
        } else if (eventTemplateId === (DuluxEventTypes.Appointments as Uuid)) {
            statusName = resolveStatusFromRecord(
                event.additionalColumnValues[DULUX__EVENT__APPOINTMENT__APPOINTMENT_STATUS__COLUMN_ID],
                appointmentStatuses,
                "",
            );
        } else if (eventTemplateId === (DuluxEventTypes.Enquiry as Uuid)) {
            statusName = resolveStatusFromRecord(
                event.additionalColumnValues[DULUX__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID],
                enquiryStatuses,
                "",
            );
        } else if (eventTemplateId === (DuluxEventTypes.Onboarding as Uuid)) {
            statusName = resolveStatusFromRecord(
                event.additionalColumnValues[DULUX__EVENT__ONBOARDING__ONBOARDING_STATUS__COLUMN_ID],
                onboardingStatuses,
                "",
            );
        }
        if (statusName === "") {
            statusName = "Active";
        }
        return listStatusFromPillType(statusName, inferListStatusPillType(statusName));
    },
    getJourneysListStatusBoard: (page) => (page === "inQueue" ? IN_QUEUE_LIST_STATUS_BOARD : null),
    resolveJourneyListStatus: (journey) => {
        const raw = journey.additionalColumnValues[DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID];
        const idStr = raw === undefined || raw === null ? "" : String(raw).trim();
        const entry =
            idStr === ""
                ? undefined
                : Object.values(leadStatuses).find((s) => String(s.id) === idStr);
        const name = entry?.displayName ?? (idStr !== "" ? idStr : "Open Lead");
        const pillType = resolveLeadStatusPill(idStr, journey, [String(DuluxLeadStatusEnum.CHURNED)]);
        return listStatusFromPillType(name, pillType);
    },
};

export function resolveWorkspaceListSupport(
    workspace: {displayName: string},
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>,
): WorkspaceListSupport | null {
    if (isAmaronWorkspace(workspaceCapabilities) === true) {
        return AMARON_LIST_SUPPORT;
    }
    if (isDuluxWorkspace(workspaceCapabilities) === true) {
        return DULUX_LIST_SUPPORT;
    }
    if (isEmotoradWorkspace(workspace) === true) {
        return null;
    }
    return null;
}

export function isDuluxListEventTemplate(eventTemplateId: Uuid): boolean {
    return (
        eventTemplateId === (DuluxEventTypes.Calls as Uuid)
        || eventTemplateId === (DuluxEventTypes.Appointments as Uuid)
        || eventTemplateId === (DuluxEventTypes.Enquiry as Uuid)
        || eventTemplateId === (DuluxEventTypes.Onboarding as Uuid)
    );
}

export function isRegistryListEventTemplate(
    support: WorkspaceListSupport | null,
    eventTemplateId: Uuid,
): boolean {
    return support !== null && support.isListEventTemplate(eventTemplateId);
}

export function isAmaronListEventTemplate(eventTemplateId: Uuid): boolean {
    return (
        eventTemplateId === (AmaronEventTypes.Calls as Uuid)
        || eventTemplateId === (AmaronEventTypes.Enquiry as Uuid)
        || eventTemplateId === (AmaronEventTypes.Service as Uuid)
    );
}

export function resolveJourneysListConfig(
    support: WorkspaceListSupport | null,
    page: JourneysListPageKey,
): ListModuleConfig | null {
    if (support === null) {
        return null;
    }
    return support.journeys[page] ?? null;
}

export function resolveEventsListConfig(support: WorkspaceListSupport | null): ListModuleConfig | null {
    return support?.events ?? null;
}
